import axios from "axios";
import { getApiErrorMessage } from "./apiErrorMessage";
import { API_BASE_URL } from "./config";
import { markPendingSamlSignIn } from "../samlSignIn";

// Single source of truth for the REST base. config.js resolves the API env vars
// (ATTICUS -> JURIST -> default) and strips trailing slashes, so a slash-terminated
// value like "https://api-dev.juristai.org/" can never produce "//auth/login/",
// and login/registration always target the same host as every other API call.
const BASE_URL = API_BASE_URL;

const EMAIL_NOT_CONFIRMED_ERROR_MESSAGE =
  'Authentication failed: An error occurred (UserNotConfirmedException) when calling the InitiateAuth operation: User is not confirmed.';
const EMAIL_VERIFICATION_REQUEST_TIMEOUT_MS = 15000;

export const logInEmail = async (email, password) => {
  console.log('--------- AUTH ---------');
  console.log('base url', BASE_URL);
  try {
    const response = await axios.post(`${BASE_URL}/auth/login/`, { email, password });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      }
    }

    if (response?.data?.non_field_errors?.[0] === EMAIL_NOT_CONFIRMED_ERROR_MESSAGE) {
      console.log('------this is the email not confirmed error message-----');
      return {
        success: false,
        message: "Email not confirmed. Please check your email to confirm."
      }
    }

    if (response.data && response.data.access && response.data.refresh) {
      const accessToken = response.data.access;
      const refreshToken = response.data.refresh;
      const user = response.data.user;
      const accessExpiration = response.data.access_expiration;
      const refreshExpiration = response.data.refresh_expiration;

      // Save the tokens and user details for future use (e.g., in localStorage or state)
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('accessExpiration', accessExpiration);
      localStorage.setItem('refreshExpiration', refreshExpiration);

      console.log("Login successful:", user);

      return {
        success: true,
        data: response.data
      };
    } else {
      return {
        success: false,
        message: "Invalid response structure"
      };
    }
  } catch (error) {
    const apiMessage = getApiErrorMessage(
      error,
      "Error logging in. Please try again.",
    );

    if (apiMessage === EMAIL_NOT_CONFIRMED_ERROR_MESSAGE) {
      console.log('------this is the email not confirmed error message-----');
      return {
        success: false,
        message: "Email not confirmed. Please check your email to confirm."
      }
    }

    return {
      success: false,
      message: apiMessage,
    };
  }
}

export const logInGoogle = async (credential, options = {}) => {
  try {
    const payload = { credential };
    for (const [key, value] of Object.entries({
      workspace_intent: options.workspaceIntent,
      setup_role: options.setupRole,
      organization_name: options.organizationName,
      booking_uid: options.bookingUid,
      invite_token: options.inviteToken,
      trial_token: options.trialToken,
      role_hint: options.roleHint,
      role: options.professionalRole,
    })) {
      if (typeof value === 'string' && value.trim()) payload[key] = value.trim();
    }
    const response = await axios.post(`${BASE_URL}/auth/google/`, payload);
    const data = response?.data;
    return data?.access && data?.refresh
      ? { success: true, data }
      : { success: false, message: 'Invalid response structure' };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Google sign-in failed. Please try again.'),
      code: error?.response?.data?.code || 'GoogleLoginFailed',
    };
  }
};

export const logInMicrosoft = async (idToken, nonce, options = {}) => {
  try {
    const payload = { id_token: idToken, nonce };
    const workspaceIntent = typeof options?.workspaceIntent === 'string' ? options.workspaceIntent.trim() : '';
    const setupRole = typeof options?.setupRole === 'string' ? options.setupRole.trim() : '';
    const organizationName = typeof options?.organizationName === 'string' ? options.organizationName.trim() : '';
    const destinationAppId = String(options?.destinationAppId || '').trim();
    if (workspaceIntent) payload.workspace_intent = workspaceIntent;
    if (setupRole) payload.setup_role = setupRole;
    if (organizationName) payload.organization_name = organizationName;
    if (['1', '2', '3', '4'].includes(destinationAppId)) payload.destination_app_id = destinationAppId;
    if (typeof options?.bookingUid === 'string' && options.bookingUid.trim()) payload.booking_uid = options.bookingUid.trim();
    if (typeof options?.inviteToken === 'string' && options.inviteToken.trim()) payload.invite_token = options.inviteToken.trim();
    if (typeof options?.trialToken === 'string' && options.trialToken.trim()) payload.trial_token = options.trialToken.trim();
    if (typeof options?.roleHint === 'string' && options.roleHint.trim()) payload.role_hint = options.roleHint.trim();
    if (typeof options?.professionalRole === 'string' && options.professionalRole.trim()) {
      payload.role = options.professionalRole.trim();
    }
    const response = await axios.post(`${BASE_URL}/auth/microsoft/`, payload);
    const data = response?.data;
    return data?.access && data?.refresh ? { success: true, data } : { success: false, message: 'Invalid response structure' };
  } catch (error) {
    return { success: false, message: getApiErrorMessage(error, 'Microsoft sign-in failed. Please try again.'), code: error?.response?.data?.code || 'MicrosoftLoginFailed', pending_token: error?.response?.data?.pending_token };
  }
};

export const logInOkta = async (idToken, nonce, trialToken = "") => {
  try {
    const response = await axios.post(BASE_URL + "/auth/okta/", { id_token: idToken, nonce, ...(trialToken ? { trial_token: trialToken } : {}) });
    return response?.data?.access && response?.data?.refresh ? { success: true, data: response.data } : { success: false, message: "Okta sign-in failed." };
  } catch (error) { return { success: false, message: getApiErrorMessage(error, "Okta sign-in failed."), code: error?.response?.data?.code }; }
};

export const getOktaConfiguration = async (email) => {
  try { const response = await axios.post(BASE_URL + "/auth/okta/config/", { email }); return { success: Boolean(response?.data?.issuer && response?.data?.client_id), data: response?.data }; }
  catch (error) { return { success: false, message: getApiErrorMessage(error, "Okta sign-in is not configured for this firm.") }; }
};

export const startSamlSignIn = async (email) => {
  try {
    const response = await axios.post(BASE_URL + "/auth/saml/start/", { email });
    if (!response?.data?.authorization_url) return { success: false, message: "Firm SSO is not configured." };
    markPendingSamlSignIn(); window.location.assign(response.data.authorization_url); return { success: true };
  } catch (error) { return { success: false, message: getApiErrorMessage(error, "Firm SSO could not be started.") }; }
};

export const logInSaml = async (code, state, trialToken = "") => {
  try {
    const response = await axios.post(BASE_URL + "/auth/saml/", { code, state, ...(trialToken ? { trial_token: trialToken } : {}) });
    return response?.data?.access && response?.data?.refresh ? { success: true, data: response.data } : { success: false, message: "Firm SSO sign-in failed." };
  } catch (error) { return { success: false, message: getApiErrorMessage(error, "Firm SSO sign-in failed.") }; }
};

export const completeMicrosoftEmail = async (pendingToken, email, options = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/auth/microsoft/complete-email/`, {
      pending_token: pendingToken,
      email,
      ...(typeof options?.bookingUid === 'string' && options.bookingUid.trim()
        ? { booking_uid: options.bookingUid.trim() }
        : {}),
    });
    return { success: response?.status === 202, data: response?.data };
  } catch (error) {
    return {
      success: false,
      code: error?.response?.data?.code,
      message: getApiErrorMessage(error, 'Microsoft email verification could not be started.'),
    };
  }
};

export const registerEmail = async (
  email,
  password1,
  password2,
  firstName,
    lastName,
    redirectUrl,
    inviteToken,
    options = {}
) => {
  try {
    const payload = { email, password1, password2, first_name: firstName, last_name: lastName };
    const normalizedRedirectUrl = typeof redirectUrl === 'string' ? redirectUrl.trim() : '';
    const normalizedInviteToken = typeof inviteToken === 'string' ? inviteToken.trim() : '';
    const normalizedAccountType =
      typeof options?.accountType === 'string' ? options.accountType.trim() : '';
    const normalizedProfessionalRole =
      typeof options?.professionalRole === 'string' ? options.professionalRole.trim() : '';
    const normalizedWorkspaceIntent =
      typeof options?.workspaceIntent === 'string' ? options.workspaceIntent.trim() : '';
    const normalizedSetupRole =
      typeof options?.setupRole === 'string' ? options.setupRole.trim() : '';
    const normalizedOrganizationName =
      typeof options?.organizationName === 'string' ? options.organizationName.trim() : '';

    if (normalizedRedirectUrl) {
      payload.redirect_url = normalizedRedirectUrl;
    }

    if (normalizedInviteToken) {
      payload.invite_token = normalizedInviteToken;
    }

    if (normalizedAccountType) {
      payload.account_type = normalizedAccountType;
    }

    if (normalizedProfessionalRole) {
      payload.role = normalizedProfessionalRole;
    }

    if (normalizedWorkspaceIntent) {
      payload.workspace_intent = normalizedWorkspaceIntent;
    }

    if (normalizedSetupRole) {
      payload.setup_role = normalizedSetupRole;
    }

    if (normalizedOrganizationName) {
      payload.organization_name = normalizedOrganizationName;
    }

    if (typeof options?.bookingUid === 'string' && options.bookingUid.trim()) {
      payload.booking_uid = options.bookingUid.trim();
    }

    if (typeof options?.trialToken === 'string' && options.trialToken.trim()) {
      payload.trial_token = options.trialToken.trim();
    }

    if (typeof options?.roleHint === 'string' && options.roleHint.trim()) {
      payload.role_hint = options.roleHint.trim();
    }

    const response = await axios.post(`${BASE_URL}/auth/registration/`, payload);
    console.log('response', response);
    return {
      success: true,
      data: response
    }
  } catch (error) {
    console.error('Error registering email:', error);
    const errorMessage = getApiErrorMessage(
      error,
      "Error registering email. Please check all values and try again.",
    );
    const code = error?.response?.data?.code;
    return {
      success: false,
      message: errorMessage,
      code: Array.isArray(code) ? code[0] : code,
    }
  }
}

export const resendVerificationEmail = async (email) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/auth/registration/resend-email/`,
      { email },
      { timeout: EMAIL_VERIFICATION_REQUEST_TIMEOUT_MS },
    );
    return {
      success: true,
      data: response?.data,
    };
  } catch (error) {
    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.non_field_errors?.[0] ||
      "Could not resend verification email.";
    return {
      success: false,
      message,
    };
  }
};

export const clearSignupRedirectUrl = async (accessToken) => {
  try {
    if (!accessToken) {
      return { success: false, message: "Access token is required to clear signup redirect." };
    }

    const response = await axios.patch(
      `${BASE_URL}/auth/user/`,
      { signup_redirect_url: null },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    return {
      success: true,
      data: response?.data,
    };
  } catch (error) {
    console.error('Error clearing signup redirect url:', error);
    return {
      success: false,
      message: "Error clearing signup redirect url.",
    };
  }
}

export const confirmAccount = async (token) => {
  try {
    const URL = `${BASE_URL}/auth/registration/verify-email/`;
    const response = await axios.post(
      URL,
      { key: token },
      { timeout: EMAIL_VERIFICATION_REQUEST_TIMEOUT_MS },
    );
    return {
      success: true,
      data: response?.data,
    };
  } catch (error) {
    console.error('Error confirming account:', error);
    return {
      success: false,
      message: getApiErrorMessage(
        error,
        'We could not confirm this email address. Request a new confirmation link and try again.',
      ),
    }
  }
}
